import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  ReferenceLine, BarChart, Bar, ScatterChart, Scatter, ZAxis, LabelList,
} from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse, faChartLine, faCheckToSlot, faMapLocationDot, faUsers, faTableList, faChartPie, faUserTie,
  faTriangleExclamation, faRotate, faMagnifyingGlass, faXmark, faArrowUp, faArrowDown, faBolt, faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

/**
 * AP Vote Share Tracker - Project Andhra Pradesh (party-first view).
 * Data: /api/voteshare?from=YYYY-MM-DD&to=YYYY-MM-DD (statistics computed on the server).
 * All changes are shown in %.
 */

const API_URL = process.env.REACT_APP_API_URL;

/* ---------------------------------- theme ---------------------------------- */
const T = {
  bg: "#f5f6fa", card: "#ffffff", ink: "#0f172a", sub: "#475569", muted: "#94a3b8", line: "#e7e9f1",
  side: "#112D4E", accent: "#4f46e5", accentSoft: "#eef2ff", up: "#059669", upSoft: "#ecfdf5", down: "#dc2626",
  downSoft: "#fef2f2", warn: "#d97706", warnSoft: "#fffbeb", head: "#4338ca",
};
const FONT = "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const PARTY_COLOR = { TDP: "#f59e0b", YSRCP: "#2563eb", JSP: "#dc2626", BJP: "#ea580c", INC: "#0ea5e9", Others: "#94a3b8", NDA: "#f59e0b", INDIA: "#0ea5e9" };
const LEADER = { CBN: "HCM Chandrababu Naidu", JMR: "YS Jagan Mohan Reddy", PK: "DCM Pawan Kalyan", LN: "HITM Nara Lokesh", Others: "Others" };
const LEADER_COLOR = { CBN: "#f59e0b", JMR: "#2563eb", PK: "#dc2626", LN: "#9333ea", Others: "#94a3b8" };
const PARTIES = ["TDP", "YSRCP", "JSP", "BJP", "INC", "Others"];
const LEADERS = ["CBN", "JMR", "PK", "LN", "Others"];
const SEAT_PARTIES = ["TDP", "JSP", "BJP", "YSRCP"];
const STRENGTHS = ["Comfortable lead", "Narrow lead", "Too close to call"];
const pc = (k) => PARTY_COLOR[k] || "#64748b";

/* --------------------------------- helpers --------------------------------- */
const pct = (v, d = 1) => (v == null || !Number.isFinite(v) ? "—" : `${(v * 100).toFixed(d)}%`);
const chg = (v, d = 1) => (v == null || !Number.isFinite(v) ? "—" : `${v > 0 ? "+" : ""}${Number(v).toFixed(d)}%`);
const dShort = (iso) => (iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", timeZone: "UTC" }) : "");
const has = (o, q) => !q || JSON.stringify(o).toLowerCase().includes(q);
const tone = (v) => (v == null ? T.muted : v > 0 ? T.up : v < 0 ? T.down : T.muted);
const lighten = (hex, a) => {
  const m = hex.replace("#", "").match(/../g).map((h) => parseInt(h, 16));
  return `rgb(${m.map((c) => Math.round(c + (255 - c) * a)).join(",")})`;
};

const TH = { background: T.head, color: "#fff", fontWeight: 700, fontSize: 12, textAlign: "center", verticalAlign: "middle",
  padding: "10px 8px", whiteSpace: "nowrap", position: "sticky", top: 0, zIndex: 1 };
const TD = { padding: "9px 8px", borderBottom: `1px solid ${T.line}`, textAlign: "center", verticalAlign: "middle", color: T.sub, fontSize: 13, whiteSpace: "nowrap" };

/* ------------------------------ small pieces ------------------------------ */

const Card = ({ title, sub, right, children, pad = 20, style }) => (
  <section style={{ background: T.card, borderRadius: 16, border: `1px solid ${T.line}`, boxShadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.04)", ...style }}>
    {(title || right) && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, padding: `${pad - 2}px ${pad}px 0`, flexWrap: "wrap" }}>
        <div>
          {title && <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>{title}</div>}
          {sub && <div style={{ fontSize: 12.5, color: T.sub, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
        </div>
        {right}
      </div>
    )}
    <div style={{ padding: pad }}>{children}</div>
  </section>
);

const Chip = ({ children, color = T.sub, bg }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 99, padding: "2px 9px", fontSize: 11.5, fontWeight: 700,
    color, background: bg || `${color}14`, whiteSpace: "nowrap" }}>{children}</span>
);

const Delta = ({ v, unit = "%" }) => (v == null ? null : v === 0 ? <Chip>No change</Chip> : (
  <Chip color={v >= 0 ? T.up : T.down} bg={v >= 0 ? T.upSoft : T.downSoft}>
    <FontAwesomeIcon icon={v >= 0 ? faArrowUp : faArrowDown} style={{ fontSize: 9 }} />
    {unit === "%" ? chg(Math.abs(v)).replace("+", "") : `${Math.abs(v)}${unit}`}
  </Chip>
));

const Empty = ({ children }) => (
  <div style={{ border: `1px dashed ${T.line}`, borderRadius: 12, padding: 22, color: T.sub, fontSize: 13, textAlign: "center", background: "#fafbfd" }}>{children}</div>
);

/** "How to read this" note shown at the end of each section (replaces the methodology tab). */
const Note = ({ children }) => (
  <div style={{ display: "flex", gap: 10, background: "#f8fafc", border: `1px solid ${T.line}`, borderRadius: 12, padding: "12px 14px", fontSize: 12.5, color: T.sub, lineHeight: 1.6 }}>
    <FontAwesomeIcon icon={faCircleInfo} style={{ color: T.accent, marginTop: 3 }} />
    <div><strong style={{ color: T.ink }}>How to read this. </strong>{children}</div>
  </div>
);

const Tip = ({ active, payload, label: lab, fmt }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 10, padding: "8px 11px", boxShadow: "0 8px 24px rgba(16,24,40,.12)", fontSize: 12, fontFamily: FONT }}>
      <div style={{ fontWeight: 700, color: T.ink, marginBottom: 4 }}>{lab}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", gap: 12, justifyContent: "space-between", minWidth: 160 }}>
          <span style={{ color: p.color || p.fill, fontWeight: 600 }}>{p.name}</span><strong>{fmt ? fmt(p.value, p.dataKey) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

/** Label drawn only at the last point of a line, so every line is named and valued without clutter. */
const EndLabel = ({ x, y, index, value, count, color, fmt }) => {
  if (index !== count - 1 || value == null) return null;
  return <text x={x + 8} y={y + 4} fill={color} style={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{fmt(value)}</text>;
};

const KPI = ({ title, value, delta, deltaUnit, deltaLabel, spark, color }) => (
  <div style={{ background: T.card, borderRadius: 16, border: `1px solid ${T.line}`, padding: "16px 18px 10px", boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    display: "flex", flexDirection: "column", gap: 6, minHeight: 134, borderTop: color ? `3px solid ${color}` : undefined }}>
    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.sub }}>{title}</div>
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: T.ink, letterSpacing: "-.5px" }}>{value}</div>
      <Delta v={delta} unit={deltaUnit || "%"} />
    </div>
    <div style={{ fontSize: 11.5, color: T.sub }}>{deltaLabel}</div>
    {spark && spark.length > 1 && (
      <div style={{ height: 32, marginTop: "auto" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={spark} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <YAxis hide domain={([lo, hi]) => { const mid = (lo + hi) / 2; const half = Math.max((hi - lo) / 2, 0.01); return [mid - half * 1.15, mid + half * 1.15]; }} />
            <Area type="linear" dataKey="v" stroke={color || T.accent} strokeWidth={2} fill={color || T.accent} fillOpacity={0.12} isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);

/* ---------------------------- party seat scoreboard ---------------------------- */

function Scoreboard({ seats }) {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {SEAT_PARTIES.map((p) => {
        const b = seats.byParty[p];
        if (!b) return null;
        const diff = b.ahead - b.won2024;
        const data = [{ name: p, ...Object.fromEntries(STRENGTHS.map((s) => [s, b[s]])) }];
        return (
          <div key={p} style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 14, alignItems: "center", padding: "10px 12px", border: `1px solid ${T.line}`, borderRadius: 12 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: pc(p) }}>{p}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, lineHeight: 1.15 }}>{b.ahead}<span style={{ fontSize: 13, color: T.sub, fontWeight: 600 }}> {p === "YSRCP" ? "seats ahead" : `of ${b.contested}`}</span></div>
              <div style={{ fontSize: 12, color: T.sub }}>Won {b.won2024} in 2024 <Delta v={diff} unit={Math.abs(diff) === 1 ? " seat" : " seats"} /></div>
            </div>
            <div>
              <div style={{ height: 34 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} layout="vertical" margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                    <XAxis type="number" hide domain={[0, Math.max(b.ahead, 1)]} />
                    <YAxis type="category" dataKey="name" hide />
                    {STRENGTHS.map((s, i) => (
                      <Bar key={s} dataKey={s} stackId="a" fill={i === 0 ? pc(p) : i === 1 ? lighten(pc(p), 0.5) : "#cbd5e1"} isAnimationActive={false}>
                        <LabelList dataKey={s} position="center" style={{ fontSize: 12, fontWeight: 800, fill: i === 0 ? "#fff" : T.ink }} formatter={(v) => (v ? v : "")} />
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 11.5, color: T.sub, marginTop: 4, flexWrap: "wrap" }}>
                <span>Comfortable lead <strong style={{ color: T.ink }}>{b["Comfortable lead"]}</strong></span>
                <span>Narrow lead <strong style={{ color: T.ink }}>{b["Narrow lead"]}</strong></span>
                <span>Too close to call <strong style={{ color: T.ink }}>{b["Too close to call"]}</strong></span>
                {b.dependent > 0 && <span style={{ color: T.warn }}>Needs alliance votes in <strong>{b.dependent}</strong></span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------- seat picture -------------------------------- */

const seatFill = (a) => (a.strength === "Too close to call" ? "#cbd5e1" : a.strength === "Narrow lead" ? lighten(pc(a.projected), 0.5) : pc(a.projected));

function Hemicycle({ acs, onOpen, height = 300 }) {
  const seats = useMemo(() => {
    const rank = (a) => {
      const i = SEAT_PARTIES.indexOf(a.projected);
      const s = STRENGTHS.indexOf(a.strength);
      if (a.strength === "Too close to call") return 30;
      return a.projected === "YSRCP" ? 40 + (2 - s) : i * 10 + s;
    };
    const order = [...acs].sort((a, b) => rank(a) - rank(b) || (b.margin || 0) - (a.margin || 0));
    const N = order.length;
    if (!N) return [];
    const rows = N > 120 ? 8 : N > 60 ? 6 : 4;
    const radii = Array.from({ length: rows }, (_, i) => 0.42 + (0.58 * i) / (rows - 1));
    const total = radii.reduce((s, r) => s + r, 0);
    const counts = radii.map((r) => Math.round((N * r) / total));
    counts[rows - 1] += N - counts.reduce((s, c) => s + c, 0);
    const pos = [];
    radii.forEach((r, i) => { for (let j = 0; j < counts[i]; j++) { const ang = Math.PI - (Math.PI * (j + 0.5)) / counts[i]; pos.push({ ang, x: r * Math.cos(ang), y: r * Math.sin(ang), r }); } });
    pos.sort((a, b) => b.ang - a.ang || a.r - b.r);
    return order.map((a, i) => ({ a, ...pos[i] }));
  }, [acs]);
  const W = 600; const H = 320; const cx = W / 2; const cy = H - 14; const R = 280;
  const dot = Math.max(5, Math.min(9.5, 900 / Math.max(seats.length, 1)));
  const nda = acs.filter((a) => a.projected !== "YSRCP" && a.projected !== "—").length;
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height, display: "block" }}>
        {seats.map((s) => (
          <circle key={s.a.acNo} cx={cx + s.x * R} cy={cy - s.y * R} r={dot} fill={seatFill(s.a)} stroke="#fff" strokeWidth={1.2} style={{ cursor: "pointer" }} onClick={() => onOpen(s.a)}>
            <title>{`${s.a.acNo}. ${s.a.acName} (${s.a.zone}) — ${s.a.projected}: ${s.a.strength.toLowerCase()}`}</title>
          </circle>
        ))}
        <text x={cx} y={cy - 46} textAnchor="middle" style={{ fontSize: 42, fontWeight: 800, fill: T.ink, fontFamily: FONT }}>{nda}</text>
        <text x={cx} y={cy - 22} textAnchor="middle" style={{ fontSize: 13, fill: T.sub, fontFamily: FONT }}>of {acs.length} seats: NDA party ahead</text>
        <text x={cx} y={12} textAnchor="middle" style={{ fontSize: 10.5, fill: T.muted, fontFamily: FONT }}>majority {Math.floor(acs.length / 2) + 1}</text>
      </svg>
      <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", fontSize: 12, color: T.sub, marginTop: 6 }}>
        {SEAT_PARTIES.map((p) => (
          <span key={p} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: pc(p) }} />{p} <strong style={{ color: T.ink }}>{acs.filter((a) => a.projected === p).length}</strong>
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: lighten(pc("TDP"), 0.5) }} />lighter = narrow lead</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: "#cbd5e1" }} />grey = too close to call</span>
      </div>
    </div>
  );
}

/* ---------------------------------- overview ---------------------------------- */

function Overview({ w, onOpen }) {
  const pa = w.state.party && w.state.party.state;
  const ps = w.state.party ? w.state.party.stateSeries : [];
  const gv = w.state.govt && w.state.govt.state;
  const toneCol = { good: T.up, warn: T.warn, bad: T.down, info: T.accent };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(185px, 1fr))", gap: 14 }}>
        {pa && ["TDP", "YSRCP", "JSP", "BJP"].filter((p) => pa.values[p] != null).map((p) => (
          <KPI key={p} title={`${p} vote share`} value={pct(pa.values[p])} delta={pa.vsBase[p]} deltaLabel="change since the 2024 election"
            spark={ps.map((x) => ({ v: x[p] }))} color={pc(p)} />
        ))}
        {gv && gv.values["Above Average"] != null && <KPI title="Rate state government above average" value={pct(gv.values["Above Average"], 0)}
          deltaLabel={`${pct(gv.values["Below Average"], 0)} rate it below average`} spark={w.state.govt.stateSeries.map((x) => ({ v: x["Above Average"] }))} color={T.up} />}
        <KPI title="Unusual movements today" value={w.anomalies.length} deltaLabel={`${w.anomalies.filter((a) => a.severity === "High").length} important`} color={T.down} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }} className="vs-two">
        <Card title="If an election were held today" sub="Seats where each party's candidate is ahead, against what it won in 2024.">
          {w.seats ? <Scoreboard seats={w.seats} /> : <Empty>No constituency data.</Empty>}
        </Card>
        <Card title="Seat picture" sub="Each dot is one constituency, coloured by the party ahead there. Hover for the name, click for detail.">
          {w.seats ? <Hemicycle acs={w.acs} onOpen={onOpen} /> : <Empty>No constituency data.</Empty>}
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)", gap: 16 }} className="vs-two">
        <Card title="What leadership should know">
          <div style={{ display: "grid", gap: 9 }}>
            {w.insights.map((x, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13.5, color: T.ink, lineHeight: 1.55 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: toneCol[x.tone] || T.accent, marginTop: 7, flexShrink: 0 }} />
                <span>{x.text}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Alerts" sub="Unusual movements on the last day of the selected period. Click one to open the constituency." right={<Chip color={T.down}>{w.anomalies.length}</Chip>}>
          {!w.anomalies.length ? <Empty>Nothing unusual.</Empty> : (
            <div style={{ display: "grid", gap: 8, maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
              {w.anomalies.map((a, i) => (
                <div key={i} onClick={() => a.acNo && onOpen(w.acs.find((y) => y.acNo === a.acNo))}
                  style={{ display: "flex", gap: 10, padding: "8px 10px", borderRadius: 10, background: a.severity === "High" ? T.downSoft : T.warnSoft, cursor: a.acNo ? "pointer" : "default" }}>
                  <FontAwesomeIcon icon={faBolt} style={{ color: a.severity === "High" ? T.down : T.warn, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.45 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.sub }}>{a.type}{a.zone ? ` · ${a.zone}` : ""}</div>{a.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      <Note>
        Every figure is a {w.window.replace("DMA", "")}-day rolling average, so single days move little; a steady move over days matters more.
        "Comfortable lead" means the gap is bigger than the survey's margin of error; "narrow lead" is close to it; "too close to call" means the
        gap is smaller than the survey can reliably measure. In seats contested by JSP or BJP, the NDA candidate is from that party, so the seat is counted for it.
      </Note>
    </div>
  );
}

/* --------------------------------- party trends --------------------------------- */

function LineCard({ title, sub, data, lines, fmt, refs = [], height = 280 }) {
  const n = data.length;
  return (
    <Card title={title} sub={sub}>
      {!n ? <Empty>No data in this period.</Empty> : (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 12, right: 64, left: -4, bottom: 0 }}>
              <CartesianGrid stroke="#eef0f6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: T.muted }} tickFormatter={(v) => `${Math.round(v)}%`} axisLine={false} tickLine={false} width={50}
                domain={([lo, hi]) => { const mid = (lo + hi) / 2; const half = Math.max((hi - lo) / 2 + 0.6, 2); return [Math.floor(mid - half), Math.ceil(mid + half)]; }} allowDecimals={false} />
              <Tooltip content={<Tip fmt={(v) => fmt(v)} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              {refs.map((r) => <ReferenceLine key={r.label} y={r.y} stroke={r.color} strokeDasharray="5 4" strokeOpacity={0.7}
                label={{ value: r.label, position: "insideBottomLeft", fontSize: 10.5, fill: r.color }} />)}
              {lines.map((l) => (
                <Line key={l.key} type="linear" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2.6} dot={{ r: 3 }} connectNulls isAnimationActive={false}
                  label={<EndLabel count={n} color={l.color} fmt={fmt} />} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function PartyTrends({ w }) {
  const P = w.state.party; const C = w.state.cm; const G = w.state.govt; const A = w.state.alliance;
  const map = (series, keys) => (series || []).map((p) => ({ date: dShort(p.date), ...Object.fromEntries(keys.map((k) => [k, p[k] != null ? p[k] * 100 : null])) }));
  const fmt = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
  const gfmt = (v) => (v == null ? "—" : chg(v));
  const partyKeys = PARTIES.filter((k) => P && P.stateSeries.some((p) => p[k] != null && p[k] >= 0.005));
  const base = P && P.state && P.state.baseline ? P.state.baseline.values : {};
  const gap = (P ? P.stateSeries : []).map((p) => ({ date: dShort(p.date), gap: p.TDP != null && p.YSRCP != null ? (p.TDP - p.YSRCP) * 100 : null }));
  const zoneGap = (() => {
    if (!P || !P.zoneSeries) return [];
    const zs = Object.keys(P.zoneSeries);
    const dates = [...new Set(zs.flatMap((z) => P.zoneSeries[z].map((p) => p.date)))].sort();
    return dates.map((d) => ({ date: dShort(d), ...Object.fromEntries(zs.map((z) => { const p = P.zoneSeries[z].find((x) => x.date === d); return [z, p ? (p.TDP - p.YSRCP) * 100 : null]; })) }));
  })();
  const zcol = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#64748b"];
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }} className="vs-two">
        <LineCard title="Party vote share" sub="Dashed lines show each party's 2024 election result." data={map(P && P.stateSeries, partyKeys)} fmt={fmt}
          lines={partyKeys.map((k) => ({ key: k, name: k, color: pc(k) }))}
          refs={["TDP", "YSRCP"].filter((k) => base[k] != null).map((k) => ({ y: base[k] * 100, color: pc(k), label: `${k} 2024: ${pct(base[k])}` }))} />
        <LineCard title="TDP lead over YSRCP" sub={base.TDP != null ? `In the 2024 election the gap was ${chg((base.TDP - base.YSRCP) * 100)}.` : ""} data={gap} fmt={gfmt}
          lines={[{ key: "gap", name: "TDP minus YSRCP", color: pc("TDP") }]} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }} className="vs-two">
        <LineCard title="Preferred Chief Minister" sub="Whom people want as Chief Minister." data={map(C && C.stateSeries, LEADERS)} fmt={fmt}
          lines={LEADERS.filter((k) => k !== "Others" && C && C.stateSeries.some((p) => p[k] != null)).map((k) => ({ key: k, name: LEADER[k], color: LEADER_COLOR[k] }))} />
        <LineCard title="State government rating" sub="Share rating the government above average and below average." data={map(G && G.stateSeries, ["Above Average", "Below Average"])} fmt={fmt}
          lines={[{ key: "Above Average", name: "Above average", color: T.up }, { key: "Below Average", name: "Below average", color: T.down }]} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }} className="vs-two">
        <LineCard title="TDP lead over YSRCP, by zone" data={zoneGap} fmt={gfmt} lines={Object.keys((P && P.zoneSeries) || {}).map((z, i) => ({ key: z, name: z, color: zcol[i % zcol.length] }))} />
        <LineCard title="Alliance vote share (NDA = TDP + JSP + BJP)" data={map(A && A.stateSeries, ["NDA", "YSRCP"])} fmt={fmt}
          lines={[{ key: "NDA", name: "NDA", color: pc("NDA") }, { key: "YSRCP", name: "YSRCP", color: pc("YSRCP") }]} />
      </div>
      <Note>
        The value at the end of each line is the latest figure in the selected period. Use the From / To dates at the top to look at a different period.
        Because each point is a rolling average, lines move slowly; a line that keeps moving the same way for many days is a real shift.
      </Note>
    </div>
  );
}

/* ---------------------------------- seat outlook ---------------------------------- */

function Seats({ w, onOpen }) {
  const s = w.seats;
  if (!s) return <Card title="Seat outlook"><Empty>No constituency data.</Empty></Card>;
  const close = w.acs.filter((a) => a.strength !== "Comfortable lead").sort((a, b) => Math.abs(a.z) - Math.abs(b.z));
  const changed = w.acs.filter((a) => a.changed);
  const zoneData = s.byZone.map((z) => ({ zone: z.zone, TDP: z.TDP, JSP: z.JSP, BJP: z.BJP, YSRCP: z.YSRCP }));
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }} className="vs-two">
        <Card title="Seats by party" sub="Seats where each party's candidate is ahead today."><Scoreboard seats={s} /></Card>
        <Card title="Seat picture" sub="Each dot is one constituency."><Hemicycle acs={w.acs} onOpen={onOpen} /></Card>
      </div>
      <Card title="Seats by zone" sub="Number of seats where each party's candidate is ahead, by zone.">
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={zoneData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid stroke="#eef0f6" vertical={false} />
              <XAxis dataKey="zone" tick={{ fontSize: 12, fill: T.ink }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip fmt={(v) => `${v} seats`} />} cursor={{ fill: "rgba(79,70,229,.04)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              {SEAT_PARTIES.map((p) => (
                <Bar key={p} dataKey={p} fill={pc(p)} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey={p} position="top" style={{ fontSize: 11.5, fontWeight: 700, fill: T.ink }} formatter={(v) => (v ? v : "")} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card title={`Close contests (${close.length})`} sub="Seats where the lead is narrow or too close to call, closest first. Click a row for detail.">
        {!close.length ? <Empty>No close contests.</Empty> : (
          <div style={{ overflow: "auto", maxHeight: 440, borderRadius: 12, border: `1px solid ${T.line}` }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
              <thead><tr>{["Constituency", "Zone", "NDA candidate's party", "Won in 2024", "Ahead now", "Gap", "Survey margin of error", "How close"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>{close.map((a) => (
                <tr key={a.acNo} onClick={() => onOpen(a)} style={{ cursor: "pointer" }}>
                  <td style={{ ...TD, fontWeight: 700, color: T.ink }}>{a.acName}</td><td style={TD}>{a.zone}</td>
                  <td style={TD}><Chip color={pc(a.contestParty)}>{a.contestParty}</Chip></td>
                  <td style={TD}><Chip color={pc(a.winner2024)}>{a.winner2024}</Chip></td>
                  <td style={TD}><Chip color="#fff" bg={pc(a.projected)}>{a.projected}</Chip></td>
                  <td style={{ ...TD, fontWeight: 800 }}>{chg(Math.abs(a.margin))}</td>
                  <td style={TD}>±{a.moe}%</td>
                  <td style={TD}><Chip color={a.strength === "Too close to call" ? T.down : T.warn}>{a.strength}</Chip></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Card>
      {changed.length > 0 && (
        <Card title={`Seats changing hands since 2024 (${changed.length})`}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {changed.map((a) => (
              <button key={a.acNo} type="button" onClick={() => onOpen(a)} style={{ border: `1px solid ${T.line}`, background: "#fff", borderRadius: 10, padding: "7px 11px", cursor: "pointer", fontFamily: FONT, fontSize: 13 }}>
                <strong>{a.acName}</strong> <span style={{ color: T.sub }}>{a.winner2024} → </span><strong style={{ color: pc(a.projected) }}>{a.projected}</strong>
              </button>
            ))}
          </div>
        </Card>
      )}
      <Note>
        A seat is counted for the party whose candidate is ahead: in TDP seats that is TDP, in JSP and BJP seats it is JSP or BJP (the NDA candidate), otherwise YSRCP.
        "Gap" is the difference between the NDA candidate's alliance and YSRCP. "Survey margin of error" is how much the figure could vary because only a sample of voters is interviewed;
        when the gap is smaller than this, the seat is too close to call.
      </Note>
    </div>
  );
}

/* --------------------------------- zones & PCs --------------------------------- */

function Shares100({ rows, keys, colorOf, nameOf, height }) {
  const data = rows.map((r) => ({ name: r.name, ...Object.fromEntries(keys.map((k) => [k, r.v[k] != null ? Math.round(r.v[k] * 1000) / 10 : 0])) }));
  return (
    <div style={{ height: height || Math.max(120, data.length * 30 + 40) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" stackOffset="expand" margin={{ top: 0, right: 8, left: 4, bottom: 0 }} barCategoryGap={5}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12, fill: T.ink }} interval={0} axisLine={false} tickLine={false} />
          <Tooltip content={<Tip fmt={(v) => `${v}%`} />} cursor={{ fill: "rgba(79,70,229,.04)" }} />
          <Legend wrapperStyle={{ fontSize: 11.5 }} iconType="circle" />
          {keys.map((k) => (
            <Bar key={k} dataKey={k} name={nameOf ? nameOf(k) : k} stackId="s" fill={colorOf(k)} isAnimationActive={false}>
              <LabelList dataKey={k} position="center" style={{ fontSize: 11, fontWeight: 700, fill: "#fff" }} formatter={(v) => (v >= 6 ? `${Math.round(v)}%` : "")} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RegionTable({ rows, showZone }) {
  if (!rows.length) return <Empty>No data.</Empty>;
  const keys = PARTIES.filter((k) => rows[0].values[k] != null);
  return (
    <div style={{ overflow: "auto", maxHeight: 520, borderRadius: 12, border: `1px solid ${T.line}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
        <thead><tr>{[showZone ? "Parliament constituency" : "Zone", ...(showZone ? ["Zone"] : []), "Interviews", ...keys, "Party ahead", "TDP since 2024", "YSRCP since 2024", "TDP change today"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.label}>
            <td style={{ ...TD, fontWeight: 700, color: T.ink }}>{r.label}</td>
            {showZone && <td style={TD}>{r.zone || "—"}</td>}
            <td style={TD}>{r.sample != null ? r.sample.toLocaleString("en-IN") : "—"}</td>
            {keys.map((k) => <td key={k} style={{ ...TD, fontWeight: k === "TDP" || k === "YSRCP" ? 800 : 500, color: k === "TDP" || k === "YSRCP" ? T.ink : T.sub }}>{pct(r.values[k])}</td>)}
            <td style={TD}>{r.leading ? <Chip color="#fff" bg={pc(r.leading)}>{r.leading}</Chip> : "—"}</td>
            <td style={{ ...TD, fontWeight: 700, color: tone(r.vsBase.TDP) }}>{chg(r.vsBase.TDP)}</td>
            <td style={{ ...TD, fontWeight: 700, color: tone(r.vsBase.YSRCP) }}>{chg(r.vsBase.YSRCP)}</td>
            <td style={{ ...TD, color: tone(r.d1.TDP) }}>{chg(r.d1.TDP)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Regions({ w }) {
  const P = w.state.party;
  if (!P) return <Card title="Zones & PCs"><Empty>No party table found.</Empty></Card>;
  const zones = [...P.zones].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  const pcs = [...P.pcs].sort((a, b) => (b.values.TDP || 0) - (a.values.TDP || 0));
  const keys = ["TDP", "YSRCP", "JSP", "BJP", "INC", "Others"].filter((k) => zones[0] && zones[0].values[k] != null);
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card title="Party vote share by zone">
        <Shares100 rows={zones.map((z) => ({ name: z.label, v: z.values }))} keys={keys} colorOf={pc} />
      </Card>
      <Card title="Zones"><RegionTable rows={zones} /></Card>
      <Card title="Party vote share by Parliament constituency">
        <Shares100 rows={pcs.map((z) => ({ name: z.label, v: z.values }))} keys={keys} colorOf={pc} />
      </Card>
      <Card title="Parliament constituencies"><RegionTable rows={pcs} showZone /></Card>
      <Note>Bars show how each area's vote divides between parties (labels below 6% are hidden to keep the bars readable; hover to see them).
        "Since 2024" compares today's share with the 2024 election result in that area.</Note>
    </div>
  );
}

/* -------------------------------- voter groups -------------------------------- */

function Swing({ rows }) {
  const data = rows.map((r) => ({ name: r.name, TDP: r.tdp, YSRCP: r.ysrcp }));
  return (
    <div style={{ height: Math.max(120, data.length * 34 + 40) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 46, left: 4, bottom: 0 }} barGap={2}>
          <CartesianGrid stroke="#eef0f6" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10.5, fill: T.muted }} tickFormatter={(v) => `${Math.round(v)}%`} axisLine={false} tickLine={false} allowDecimals={false}
            domain={[(lo) => Math.floor(Math.min(lo, 0) * 1.35 - 1), (hi) => Math.ceil(Math.max(hi, 0) * 1.35 + 1)]} />
          <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12, fill: T.ink }} interval={0} axisLine={false} tickLine={false} />
          <ReferenceLine x={0} stroke="#cbd5e1" />
          <Tooltip content={<Tip fmt={(v) => chg(v)} />} cursor={{ fill: "rgba(79,70,229,.04)" }} />
          <Legend wrapperStyle={{ fontSize: 11.5 }} iconType="circle" />
          {["TDP", "YSRCP"].map((k) => (
            <Bar key={k} dataKey={k} name={`${k} since 2024`} fill={pc(k)} radius={3} isAnimationActive={false}>
              <LabelList dataKey={k} content={({ x, y, width, height, value }) => (value == null ? null : (
                <text x={value >= 0 ? x + width + 4 : x + width - 4} y={y + height / 2 + 4} textAnchor={value >= 0 ? "start" : "end"}
                  style={{ fontSize: 10.5, fontWeight: 700, fill: T.ink, fontFamily: FONT }}>{chg(value)}</text>
              ))} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function GroupCard({ group, party, cm, govt }) {
  const segsP = party.filter((s) => s.group === group);
  const order = segsP.map((s) => s.label);
  const segsC = cm.filter((s) => s.group === group).sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
  const segsG = govt.filter((s) => s.group === group).sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
  const pk = PARTIES.filter((k) => segsP.some((s) => s.values[k] != null));
  const ck = LEADERS.filter((k) => segsC.some((s) => s.values[k] != null));
  const gData = segsG.map((s) => ({ name: s.label, v: s.values["Above Average"] != null ? Math.round(s.values["Above Average"] * 1000) / 10 : null }));
  return (
    <Card title={group} sub={`${segsP.length} groups`}>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 22 }} className="vs-two">
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Party vote share</div>
          {segsP.length ? <Shares100 rows={segsP.map((s) => ({ name: s.label, v: s.values }))} keys={pk} colorOf={pc} /> : <Empty>No data.</Empty>}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Preferred Chief Minister</div>
          {segsC.length ? <Shares100 rows={segsC.map((s) => ({ name: s.label, v: s.values }))} keys={ck} colorOf={(k) => LEADER_COLOR[k]} nameOf={(k) => LEADER[k]} /> : <Empty>No data.</Empty>}
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Change in vote share since 2024</div>
          <Swing rows={segsP.map((s) => ({ name: s.label, tdp: s.vsBase.TDP, ysrcp: s.vsBase.YSRCP }))} />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Rate state government above average</div>
          {gData.length ? (
            <div style={{ height: Math.max(120, gData.length * 30 + 20) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gData} layout="vertical" margin={{ top: 0, right: 46, left: 4, bottom: 0 }} barCategoryGap={5}>
                  <XAxis type="number" hide domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12, fill: T.ink }} interval={0} axisLine={false} tickLine={false} />
                  <Tooltip content={<Tip fmt={(v) => `${v}%`} />} cursor={{ fill: "rgba(79,70,229,.04)" }} />
                  <Bar dataKey="v" name="Above average" fill={T.up} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                    <LabelList dataKey="v" position="right" style={{ fontSize: 11, fontWeight: 700, fill: T.ink }} formatter={(v) => (v == null ? "" : `${v}%`)} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty>No data.</Empty>}
        </div>
      </div>
    </Card>
  );
}

function VoterGroups({ w }) {
  const party = (w.state.party && w.state.party.segments) || [];
  const cm = (w.state.cm && w.state.cm.segments) || [];
  const govt = (w.state.govt && w.state.govt.segments) || [];
  const groups = [...new Set(party.map((s) => s.group))];
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {!groups.length ? <Card title="Voter groups"><Empty>No voter-group tables found.</Empty></Card>
        : groups.map((g) => <GroupCard key={g} group={g} party={party} cm={cm} govt={govt} />)}
      <Note>
        For every group you see four things side by side: how its vote divides between parties, whom it wants as Chief Minister, how TDP's and YSRCP's
        share has changed since the 2024 election, and how many rate the state government above average. Small groups (for example some castes) have fewer
        interviews, so their changes are less certain than large groups'.
      </Note>
    </div>
  );
}

/* ------------------------------- constituencies ------------------------------- */

const AC_COLS = [["acName", "Constituency"], ["zone", "Zone"], ["pc", "PC"], ["contestParty", "NDA candidate's party"], ["winner2024", "Won in 2024"],
  ["tdp", "TDP"], ["ysrcp_p", "YSRCP"], ["jsp", "JSP"], ["bjp", "BJP"], ["projected", "Ahead now"], ["margin", "Gap"], ["strength", "How close"],
  ["d1", "Change today"], ["cmLeader", "Most preferred CM"], ["mla", "MLA rating"]];

function Constituencies({ w, onOpen }) {
  const [q, setQ] = useState("");
  const [zone, setZone] = useState("");
  const [party, setParty] = useState("");
  const [how, setHow] = useState("");
  const [sort, setSort] = useState(["margin", 1]);
  const zones = [...new Set(w.acs.map((a) => a.zone))].sort();
  const val = (a, k) => ({ tdp: a.party.TDP, ysrcp_p: a.party.YSRCP, jsp: a.party.JSP, bjp: a.party.BJP,
    mla: (a.perf["MLA Performance"] || {}).score, margin: a.margin != null ? Math.abs(a.margin) * (a.projected === "YSRCP" ? -1 : 1) : null }[k] ?? a[k]);
  const list = w.acs.filter((a) => (!zone || a.zone === zone) && (!party || a.projected === party) && (!how || a.strength === how) && has([a.acName, a.pc, a.zone, a.acNo], q.toLowerCase()))
    .sort((a, b) => {
      const x = val(a, sort[0]); const y = val(b, sort[0]);
      if (typeof x === "string" || typeof y === "string") return String(x).localeCompare(String(y)) * sort[1];
      return ((x == null ? -1e9 : x) - (y == null ? -1e9 : y)) * sort[1];
    });
  const inp = { padding: "8px 11px", border: `1px solid ${T.line}`, borderRadius: 10, fontSize: 13, fontFamily: FONT, background: "#fff" };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card title={`All constituencies (${list.length} of ${w.acs.length})`} sub="Click a column heading to sort and a row to open the constituency.">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ position: "relative" }}>
            <FontAwesomeIcon icon={faMagnifyingGlass} style={{ position: "absolute", left: 11, top: 11, color: T.muted, fontSize: 12 }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search constituency, PC or number" style={{ ...inp, paddingLeft: 31, minWidth: 260 }} />
          </div>
          <select value={zone} onChange={(e) => setZone(e.target.value)} style={inp}><option value="">All zones</option>{zones.map((z) => <option key={z}>{z}</option>)}</select>
          <select value={party} onChange={(e) => setParty(e.target.value)} style={inp}><option value="">Any party ahead</option>{SEAT_PARTIES.map((p) => <option key={p}>{p}</option>)}</select>
          <select value={how} onChange={(e) => setHow(e.target.value)} style={inp}><option value="">Any lead</option>{STRENGTHS.map((s) => <option key={s}>{s}</option>)}</select>
        </div>
        <div style={{ overflow: "auto", maxHeight: 620, borderRadius: 12, border: `1px solid ${T.line}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
            <thead><tr>{AC_COLS.map(([k, l]) => (
              <th key={k} style={{ ...TH, cursor: "pointer" }} onClick={() => setSort(([c, d]) => [k, c === k ? -d : -1])}>{l}{sort[0] === k ? (sort[1] === 1 ? " ▲" : " ▼") : ""}</th>
            ))}</tr></thead>
            <tbody>{list.map((a) => {
              const mla = a.perf["MLA Performance"] || {};
              return (
                <tr key={a.acNo} onClick={() => onOpen(a)} style={{ cursor: "pointer" }}>
                  <td style={{ ...TD, fontWeight: 700, color: T.ink }}>{a.acNo}. {a.acName}</td>
                  <td style={TD}>{a.zone}</td><td style={TD}>{a.pc}</td>
                  <td style={TD}><Chip color={pc(a.contestParty)}>{a.contestParty}</Chip></td>
                  <td style={TD}><Chip color={pc(a.winner2024)}>{a.winner2024}</Chip></td>
                  {["TDP", "YSRCP", "JSP", "BJP"].map((k) => <td key={k} style={{ ...TD, fontWeight: k === a.contestParty || k === "YSRCP" ? 800 : 500, color: k === a.contestParty || k === "YSRCP" ? T.ink : T.sub }}>{pct(a.party[k])}</td>)}
                  <td style={TD}><Chip color="#fff" bg={pc(a.projected)}>{a.projected}</Chip></td>
                  <td style={{ ...TD, fontWeight: 800 }}>{chg(Math.abs(a.margin))}</td>
                  <td style={TD}><Chip color={a.strength === "Comfortable lead" ? T.up : a.strength === "Narrow lead" ? T.warn : T.down}>{a.strength}</Chip></td>
                  <td style={{ ...TD, color: tone(a.projected === "YSRCP" ? -a.d1 : a.d1) }}>{chg(a.d1)}</td>
                  <td style={TD}>{a.cmLeader ? LEADER[a.cmLeader] : "—"}</td>
                  <td style={TD}>{mla.rating ? <Chip color={/bad|low/i.test(mla.rating) ? T.down : T.up}>{mla.rating}</Chip> : "—"}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      </Card>
      <Note>
        "Gap" is how far the party ahead leads (NDA candidate's alliance vs YSRCP). "Change today" is how much the NDA side's gap moved since the previous day
        (positive = NDA side gained). Party columns show each party's own share; in JSP and BJP seats many NDA voters still name TDP, which is why TDP's share can be high there.
      </Note>
    </div>
  );
}

/* ------------------------------- what drives votes ------------------------------- */

function Drivers({ w }) {
  const drivers = w.drivers || [];
  const zones = [...new Set(w.acs.map((a) => a.zone))].sort();
  const zc = ["#4f46e5", "#0ea5e9", "#10b981", "#f59e0b", "#ec4899", "#64748b"];
  if (!drivers.length) return <Card title="What drives votes"><Empty>No rating measures in the sheet.</Empty></Card>;
  const bars = drivers.map((d) => ({ name: d.label, r: d.r }));
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card title="Which ratings go together with a bigger NDA lead?" sub="The longer the bar, the more closely that rating moves with the NDA side's lead across all constituencies (scale 0 to 1).">
        <div style={{ height: Math.max(180, bars.length * 44) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} layout="vertical" margin={{ top: 0, right: 60, left: 4, bottom: 0 }}>
              <XAxis type="number" domain={[0, 1]} hide />
              <YAxis type="category" dataKey="name" width={270} tick={{ fontSize: 12.5, fill: T.ink }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip fmt={(v) => v} />} cursor={{ fill: "rgba(79,70,229,.04)" }} />
              <Bar dataKey="r" name="Strength of link" fill={T.accent} radius={[0, 6, 6, 0]} isAnimationActive={false}>
                <LabelList dataKey="r" position="right" style={{ fontSize: 12, fontWeight: 800, fill: T.ink }} formatter={(v) => (v == null ? "" : v.toFixed(2))} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: 16 }}>
        {drivers.map((d) => {
          const pts = w.acs.map((a) => ({ x: a.perf[d.metric] ? Math.round(a.perf[d.metric].score * 1000) / 10 : null, y: a.margin, name: a.acName, zone: a.zone }))
            .filter((p) => p.x != null && p.y != null);
          const xs = pts.map((p) => p.x);
          const x0 = Math.min(...xs); const x1 = Math.max(...xs);
          const line = d.slope != null ? [{ x: x0, y: d.intercept + d.slope * (x0 / 100) }, { x: x1, y: d.intercept + d.slope * (x1 / 100) }] : null;
          return (
            <Card key={d.metric} title={d.label} sub={`${d.strengthText}${d.significant ? "" : " (not reliable)"} · every +10 in this rating goes with about ${chg(d.ptsPer10)} more NDA lead.`}>
              <div style={{ height: 290 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 4, right: 14, left: 4, bottom: 18 }}>
                    <CartesianGrid stroke="#eef0f6" />
                    <XAxis type="number" dataKey="x" domain={["auto", "auto"]} tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false}
                      label={{ value: "Rating score (0–100)", position: "insideBottom", offset: -8, fontSize: 11, fill: T.sub }} />
                    <YAxis type="number" dataKey="y" tick={{ fontSize: 11, fill: T.muted }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} width={48} />
                    <ZAxis range={[34, 34]} />
                    <ReferenceLine y={0} stroke="#cbd5e1" />
                    <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const p = payload[0].payload;
                      return <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 10, padding: "7px 10px", fontSize: 12, fontFamily: FONT }}>
                        <strong>{p.name}</strong> · {p.zone}<br />Rating {p.x} · NDA lead {chg(p.y)}</div>;
                    }} />
                    <Legend verticalAlign="top" height={26} wrapperStyle={{ fontSize: 11.5 }} iconType="circle" />
                    {zones.map((z, i) => <Scatter key={z} name={z} data={pts.filter((p) => p.zone === z)} fill={zc[i % zc.length]} fillOpacity={0.75} isAnimationActive={false} />)}
                    {line && <ReferenceLine segment={line} stroke={T.down} strokeWidth={2} strokeDasharray="6 4" ifOverflow="extendDomain"
                      label={{ value: `link ${d.r}`, position: "insideTopRight", fontSize: 11, fontWeight: 700, fill: T.down }} />}
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </Card>
          );
        })}
      </div>
      <Note>
        Each dot is a constituency: further right = better rating, higher up = bigger NDA-side lead. The dashed line shows the overall pattern. A strong link means
        the two move together; it does not prove that one causes the other.
      </Note>
    </div>
  );
}

/* ------------------------------- leaders vs parties ------------------------------- */

const PAIRS = [
  { leader: "CBN", party: "TDP", contest: "TDP" },
  { leader: "PK", party: "JSP", contest: "JSP" },
  { leader: "JMR", party: "YSRCP", contest: null },
];

function Dumbbell({ party, leader, pv, lv, max }) {
  const pos = (v) => `${Math.min((v / max) * 100, 100)}%`;
  const ahead = lv >= pv;
  return (
    <div style={{ position: "relative", height: 26 }}>
      <div style={{ position: "absolute", left: 0, right: 0, top: 12, height: 2, background: "#eef0f6" }} />
      <div style={{ position: "absolute", top: 11, height: 4, borderRadius: 2, left: pos(Math.min(pv, lv)), width: `calc(${pos(Math.max(pv, lv))} - ${pos(Math.min(pv, lv))})`, background: ahead ? "#a7f3d0" : "#fecaca" }} />
      <div title={`${party} vote ${pct(pv)}`} style={{ position: "absolute", top: 6, left: pos(pv), width: 14, height: 14, marginLeft: -7, borderRadius: "50%", background: "#fff", border: `3px solid ${pc(party)}` }} />
      <div title={`${LEADER[leader]} as CM ${pct(lv)}`} style={{ position: "absolute", top: 6, left: pos(lv), width: 14, height: 14, marginLeft: -7, borderRadius: "50%", background: LEADER_COLOR[leader] }} />
    </div>
  );
}

function PairDetail({ w, P }) {
  const rows = w.acs.filter((a) => (!P.contest || a.contestParty === P.contest) && a.party[P.party] != null && a.cm[P.leader] != null)
    .map((a) => ({ a, pv: a.party[P.party], lv: a.cm[P.leader], gap: (a.cm[P.leader] - a.party[P.party]) * 100 })).sort((x, y) => y.gap - x.gap);
  const ahead = rows.filter((r) => r.gap > 0).length; const behind = rows.length - ahead;
  const max = Math.max(0.1, ...rows.map((r) => Math.max(r.pv, r.lv))) * 1.08;
  const name = LEADER[P.leader];
  const summary = !rows.length ? "" : ahead === 0 ? `${name} is behind ${P.party} in every one of these ${rows.length} constituencies.`
    : behind === 0 ? `${name} is ahead of ${P.party} in every one of these ${rows.length} constituencies.` : `${name} is ahead of ${P.party} in ${ahead} constituencies and behind in ${behind}.`;
  const lists = [[ahead > 0 ? `Where ${name} is furthest ahead of ${P.party}` : `Where ${name} is closest to ${P.party}`, rows.slice(0, 8)],
    [behind > 0 ? `Where ${name} is furthest behind ${P.party}` : `Where ${name} is least ahead of ${P.party}`, rows.slice(-8).reverse()]];
  return (
    <Card title={`${name} vs ${P.party}, constituency by constituency`} sub={P.contest ? `Seats where ${P.contest} is the NDA candidate (${rows.length}).` : `All ${rows.length} constituencies.`}>
      {summary && <div style={{ marginBottom: 12, padding: "9px 12px", borderRadius: 10, fontSize: 13.5, fontWeight: 700, background: ahead >= behind ? T.upSoft : T.downSoft, color: ahead >= behind ? T.up : T.down }}>{summary}</div>}
      <div style={{ display: "flex", gap: 16, fontSize: 12, color: T.sub, marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: `3px solid ${pc(P.party)}` }} />% who will vote {P.party}</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", background: LEADER_COLOR[P.leader] }} />% who want {name} as CM</span>
      </div>
      {!rows.length ? <Empty>No constituencies to compare.</Empty> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 18 }}>
          {lists.map(([t, l]) => (
            <div key={t}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, marginBottom: 8 }}>{t}</div>
              <div style={{ display: "grid", gap: 6 }}>
                {l.map((r) => (
                  <div key={r.a.acNo} style={{ display: "grid", gridTemplateColumns: "150px 1fr 64px", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 12.5, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={`${r.a.acName} (${r.a.zone})`}>{r.a.acName}</div>
                    <Dumbbell party={P.party} leader={P.leader} pv={r.pv} lv={r.lv} max={max} />
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: tone(r.gap), textAlign: "right" }}>{chg(r.gap)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function LeadersVsParties({ w }) {
  const ps = w.state.party && w.state.party.state;
  const cs = w.state.cm && w.state.cm.state;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card title="Is the leader more popular than his party?"
        sub="The survey asks two separate questions: which party people will vote for, and whom they want as Chief Minister. If more people want the leader than vote for his party, he brings in extra support. If fewer, the party is carrying him.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))", gap: 14 }}>
          {PAIRS.map((p) => {
            const pv = ps && ps.values[p.party]; const lv = cs && cs.values[p.leader];
            if (pv == null || lv == null) return null;
            const gap = (lv - pv) * 100; const isAhead = gap >= 0;
            return (
              <div key={p.leader} style={{ border: `1px solid ${T.line}`, borderRadius: 14, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{LEADER[p.leader]} and {p.party}</div>
                <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                  {[[`Will vote ${p.party}`, pv, pc(p.party), true], [`Want ${LEADER[p.leader]} as CM`, lv, LEADER_COLOR[p.leader], false]].map(([l, v, c, light]) => (
                    <div key={l}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: T.sub }}><span>{l}</span><strong style={{ color: T.ink }}>{pct(v)}</strong></div>
                      <div style={{ height: 10, background: "#f1f3f9", borderRadius: 99, overflow: "hidden", marginTop: 3 }}>
                        <div style={{ width: `${Math.min(v / 0.6, 1) * 100}%`, height: "100%", borderRadius: 99, background: light ? lighten(c, 0.55) : c }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 10, background: isAhead ? T.upSoft : T.downSoft, color: isAhead ? T.up : T.down, fontSize: 13, fontWeight: 700 }}>
                  {isAhead ? `${LEADER[p.leader]} is ${Math.abs(gap).toFixed(1)}% more popular than ${p.party}` : `${LEADER[p.leader]} is ${Math.abs(gap).toFixed(1)}% behind ${p.party}`}
                </div>
                <div style={{ fontSize: 12, color: T.sub, marginTop: 6 }}>
                  {isAhead ? `He draws support beyond ${p.party}'s own voters.` : `Some ${p.party} voters do not want him as Chief Minister — the party is carrying him.`}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      {PAIRS.map((P) => <PairDetail key={P.leader} w={w} P={P} />)}
      <Note>
        The gap is the % who want the leader as Chief Minister minus the % who will vote for his party. {LEADER.LN} is not compared because few people
        name him as Chief Minister while TDP's vote is compared with {LEADER.CBN}.
      </Note>
    </div>
  );
}

/* --------------------------------- seats at risk --------------------------------- */

function Risks({ w, onOpen }) {
  const list = w.atRisk || [];
  const parties = ["TDP", "JSP", "BJP"].filter((p) => list.some((r) => r.heldBy === p));
  const lvl = { High: T.down, Medium: T.warn, Watch: T.muted };
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {!list.length ? <Card title="Seats at risk"><Empty>No NDA-held seat looks at risk.</Empty></Card> : parties.map((p) => {
        const rows = list.filter((r) => r.heldBy === p);
        return (
          <Card key={p} title={`${p} seats at risk (${rows.length})`} sub={`Seats ${p} won in 2024 that now look vulnerable. Click a row for detail.`}>
            <div style={{ overflow: "auto", borderRadius: 12, border: `1px solid ${T.line}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT }}>
                <thead><tr>{["Risk", "Constituency", "Zone", "PC", "Ahead now", "Gap", "How close", `${p} alone vs YSRCP`, "Trend per day", "Weak MLA ratings"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
                <tbody>{rows.map((r) => (
                  <tr key={r.acNo} onClick={() => onOpen(w.acs.find((a) => a.acNo === r.acNo))} style={{ cursor: "pointer" }}>
                    <td style={TD}><Chip color="#fff" bg={lvl[r.level]}>{r.level}</Chip></td>
                    <td style={{ ...TD, fontWeight: 700, color: T.ink }}>{r.acName}</td><td style={TD}>{r.zone}</td><td style={TD}>{r.pc}</td>
                    <td style={TD}><Chip color="#fff" bg={pc(r.projected)}>{r.projected}</Chip></td>
                    <td style={{ ...TD, fontWeight: 800 }}>{chg(Math.abs(r.margin))}</td>
                    <td style={TD}><Chip color={r.strength === "Comfortable lead" ? T.up : r.strength === "Narrow lead" ? T.warn : T.down}>{r.strength}</Chip></td>
                    <td style={{ ...TD, fontWeight: 700, color: tone(r.standalone) }}>{chg(r.standalone)}</td>
                    <td style={{ ...TD, color: tone(r.slope) }}>{chg(r.slope, 2)}</td>
                    <td style={TD}>{r.weakMla.length ? r.weakMla.map((m) => m.replace("MLA ", "")).join(", ") : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        );
      })}
      <Note>
        A seat is flagged when the NDA side's lead is thin or gone, when it is falling, when the party would be behind YSRCP on its own (without alliance votes),
        and when the MLA is rated poorly. "High" needs urgent attention; "Watch" is an early warning. The "alone vs YSRCP" column compares only that party's own vote with YSRCP's, without its alliance partners.
      </Note>
    </div>
  );
}

/* ------------------------------------ drawer ------------------------------------ */

function Drawer({ a, onClose, win }) {
  if (!a) return null;
  const data = a.series.map((p) => ({ ...p, date: dShort(p.date) }));
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.35)", zIndex: 50, display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(780px,100%)", height: "100%", background: T.bg, overflowY: "auto", fontFamily: FONT, boxShadow: "-12px 0 40px rgba(15,23,42,.2)" }}>
        <div style={{ background: "#fff", borderBottom: `1px solid ${T.line}`, padding: "18px 22px", display: "flex", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 2 }}>
          <div>
            <div style={{ fontSize: 12, color: T.sub, fontWeight: 600 }}>Constituency {a.acNo} · {a.pc} PC · {a.zone}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.ink }}>{a.acName}</div>
            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
              <Chip color={pc(a.winner2024)}>Won in 2024: {a.winner2024}</Chip><Chip color={pc(a.contestParty)}>NDA candidate: {a.contestParty}</Chip>
              <Chip color="#fff" bg={pc(a.projected)}>Ahead now: {a.projected}</Chip><Chip>{a.strength}</Chip>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "none", background: "#f1f3f9", borderRadius: 10, width: 36, height: 36, cursor: "pointer" }}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        <div style={{ padding: 18, display: "grid", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 10 }}>
            {[["Gap", chg(Math.abs(a.margin)), T.ink], ["Survey margin of error", `±${a.moe}%`, T.ink],
              [`${a.contestParty} alone vs YSRCP`, chg(a.standalone), tone(a.standalone)], ["Change today", chg(a.d1), tone(a.d1)]].map(([l, v, c]) => (
              <div key={l} style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 12px" }}>
                <div style={{ fontSize: 11.5, color: T.sub, fontWeight: 600 }}>{l}</div><div style={{ fontSize: 20, fontWeight: 800, color: c }}>{v}</div>
              </div>
            ))}
          </div>
          <Card title="Party vote share over time" sub={`${win} rolling`} pad={16}>
            <div style={{ height: 230 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 50, left: -6, bottom: 0 }}>
                  <CartesianGrid stroke="#eef0f6" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: T.muted }} tickFormatter={(v) => `${Math.round(v)}%`} axisLine={false} tickLine={false} width={46} allowDecimals={false}
                    domain={([lo, hi]) => { const mid = (lo + hi) / 2; const half = Math.max((hi - lo) / 2 + 0.6, 2); return [Math.floor(mid - half), Math.ceil(mid + half)]; }} />
                  <Tooltip content={<Tip fmt={(v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`)} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  {[["tdp", "TDP"], ["ysrcp", "YSRCP"], ["jsp", "JSP"], ["bjp", "BJP"]].map(([k, n]) => (
                    <Line key={k} type="linear" dataKey={k} name={n} stroke={pc(n)} strokeWidth={2.4} dot={{ r: 2.5 }} isAnimationActive={false}
                      label={<EndLabel count={data.length} color={pc(n)} fmt={(v) => `${Number(v).toFixed(1)}%`} />} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Preferred Chief Minister" pad={16}>
            {LEADERS.filter((k) => k !== "Others" && a.cm[k] != null).map((k) => (
              <div key={k} style={{ marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: T.sub }}>{LEADER[k]}</span><strong>{pct(a.cm[k])}</strong></div>
                <div style={{ height: 7, background: "#f1f3f9", borderRadius: 99, overflow: "hidden", marginTop: 3 }}><div style={{ width: `${a.cm[k] * 100}%`, height: "100%", background: LEADER_COLOR[k], borderRadius: 99 }} /></div>
              </div>
            ))}
          </Card>
          <Card title="Ratings" pad={14}>
            <div style={{ overflow: "auto", borderRadius: 10, border: `1px solid ${T.line}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr>{["Measure", "Score", "Rating", "Rank (of 175)"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
                <tbody>{Object.entries(a.perf).map(([m, v]) => (
                  <tr key={m}><td style={{ ...TD, fontWeight: 700, color: T.ink }}>{m}</td><td style={TD}>{v.score != null ? (v.score * 100).toFixed(1) : "—"}</td>
                    <td style={TD}><Chip color={/bad|low/i.test(v.rating) ? T.down : T.up}>{v.rating || "—"}</Chip></td><td style={TD}>{v.rank != null ? `#${v.rank}` : "—"}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------- page ------------------------------------- */

const NAV = [
  ["overview", "Overview", faHouse], ["trends", "Party trends", faChartLine], ["seats", "Seat outlook", faCheckToSlot],
  ["regions", "Zones & PCs", faMapLocationDot], ["groups", "Voter groups", faUsers], ["acs", "Constituencies", faTableList],
  ["drivers", "What drives votes", faChartPie], ["leaders", "Leaders vs parties", faUserTie], ["risks", "Seats at risk", faTriangleExclamation],
];

const CSS = `
.vs-shell { display: grid; grid-template-columns: 236px 1fr; min-height: 100vh; }
.vs-side { position: sticky; top: 0; height: 100vh; overflow-y: auto; }
.vs-navbtn:hover { background: rgba(255,255,255,.08) !important; }
.vs-wrap tbody tr:hover td { background: #f8f9ff; }
@media (max-width: 1100px) { .vs-two { grid-template-columns: 1fr !important; } }
@media (max-width: 900px) { .vs-shell { grid-template-columns: 1fr; } .vs-side { position: static; height: auto; } }
`;

const APVoteshareDashboard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [win, setWin] = useState("");
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async ({ refresh = false, f = from, t = to } = {}) => {
    setLoading(true); setErr("");
    try {
      const token = await localforage.getItem("token");
      const params = {};
      if (refresh) params.refresh = 1;
      if (f) params.from = f;
      if (t) params.to = t;
      const r = await api.get(`${API_URL}/voteshare`, { headers: { Authorization: `Bearer ${token}` }, params, timeout: 120000 });
      setData(r.data);
      setFrom(r.data.dateRange ? r.data.dateRange.from : "");
      setTo(r.data.dateRange ? r.data.dateRange.to : "");
      setWin((cur) => (cur && r.data.windows.includes(cur) ? cur : r.data.windows[0]));
    } catch (e) {
      setErr(e.response?.status === 403 ? "You do not have access to this dashboard." : (e.response?.data?.error || "Could not load the data."));
    } finally { setLoading(false); }
  };
  useEffect(() => { load({ f: "", t: "" }); document.title = "AP Vote Share Tracker"; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const w = data && win ? data.byWindow[win] : null;
  const range = (data && data.dateRange) || {};
  const dateInp = { padding: "7px 10px", border: `1px solid ${T.line}`, borderRadius: 10, fontSize: 13, fontFamily: FONT, color: T.ink };

  return (
    <div style={{ fontFamily: FONT }} className="vs-wrap">
      <style>{CSS}</style>
      <Dashboard />
      <div className="vs-shell" style={{ background: T.bg }}>
        <aside className="vs-side" style={{ background: T.side, color: "#fff", padding: "20px 0" }}>
          <div style={{ padding: "0 20px 16px", borderBottom: "1px solid rgba(255,255,255,.12)" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", opacity: 0.7 }}>PROJECT ANDHRA PRADESH</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>Vote Share Tracker</div>
          </div>
          <nav style={{ padding: "12px 0" }}>
            {NAV.map(([id, l, ic]) => (
              <button key={id} type="button" className="vs-navbtn" onClick={() => { setTab(id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", border: "none", fontFamily: FONT,
                  background: tab === id ? "rgba(255,255,255,.14)" : "transparent", color: "#fff", fontWeight: tab === id ? 700 : 500,
                  fontSize: 14, padding: "11px 20px", cursor: "pointer", borderLeft: `3px solid ${tab === id ? "#a5b4fc" : "transparent"}` }}>
                <FontAwesomeIcon icon={ic} style={{ width: 18, opacity: 0.9 }} />{l}
              </button>
            ))}
          </nav>
        </aside>

        <main style={{ minWidth: 0 }}>
          <div style={{ background: "#fff", borderBottom: `1px solid ${T.line}`, padding: "18px 26px", display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: "-.4px" }}>{(NAV.find((n) => n[0] === tab) || [])[1]}</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              {data && data.windows.length > 1 && (
                <select value={win} onChange={(e) => setWin(e.target.value)} style={dateInp}>{data.windows.map((x) => <option key={x} value={x}>{x.replace("DMA", "-day average")}</option>)}</select>
              )}
              <label style={{ fontSize: 12.5, color: T.sub, display: "flex", alignItems: "center", gap: 6 }}>From
                <input type="date" value={from} min={range.min} max={to || range.max} onChange={(e) => { setFrom(e.target.value); load({ f: e.target.value, t: to }); }} style={dateInp} />
              </label>
              <label style={{ fontSize: 12.5, color: T.sub, display: "flex", alignItems: "center", gap: 6 }}>To
                <input type="date" value={to} min={from || range.min} max={range.max} onChange={(e) => { setTo(e.target.value); load({ f: from, t: e.target.value }); }} style={dateInp} />
              </label>
              {(from !== range.min || to !== range.max) && range.min && (
                <button type="button" onClick={() => load({ f: range.min, t: range.max })} style={{ ...dateInp, background: T.accentSoft, color: T.accent, fontWeight: 700, border: "none", cursor: "pointer" }}>All dates</button>
              )}
              <button type="button" onClick={() => load({ refresh: true })} disabled={loading} title="Read the latest data from the sheets"
                style={{ ...dateInp, background: "#fff", fontWeight: 600, cursor: "pointer" }}>
                <FontAwesomeIcon icon={faRotate} spin={loading} /> Refresh
              </button>
            </div>
          </div>

          <div style={{ padding: "20px 26px 48px" }}>
            {err && <div style={{ background: T.downSoft, color: T.down, borderRadius: 12, padding: 16, fontWeight: 600, marginBottom: 16 }}>{err}</div>}
            {loading && !data && <div style={{ padding: 60, textAlign: "center", color: T.sub }}>Loading…</div>}
            {w && (
              <>
                {tab === "overview" && <Overview w={w} onOpen={setOpen} />}
                {tab === "trends" && <PartyTrends w={w} />}
                {tab === "seats" && <Seats w={w} onOpen={setOpen} />}
                {tab === "regions" && <Regions w={w} />}
                {tab === "groups" && <VoterGroups w={w} />}
                {tab === "acs" && <Constituencies w={w} onOpen={setOpen} />}
                {tab === "drivers" && <Drivers w={w} />}
                {tab === "leaders" && <LeadersVsParties w={w} />}
                {tab === "risks" && <Risks w={w} onOpen={setOpen} />}
              </>
            )}
          </div>
        </main>
      </div>
      <Drawer a={open} onClose={() => setOpen(null)} win={win} />
    </div>
  );
};

export default APVoteshareDashboard;
